import React, { useEffect, useMemo, useRef } from 'react';
import Konva from 'konva';
import { Stage, Layer, Rect, Group, Path } from 'react-konva';
import { radToDeg } from '../../lib/ep';
import { adaptImageSizes } from '../canvas/utils';

const PeelMeCanvas = (props) => {
	const refs = useRef([]);
	const start_dragging = useRef(false);
	const tutorial_timeout = useRef(false);
	const canvas_size = useRef(props?.canvas_size ?? { w:100, h:100 });
	const is_builder = props?.is_builder ?? false;
	const promotion = props?.promotion ?? false;

	const back_image_size = 1000;

	// in sec (0 for disabled)
	const max_waiting_time = parseInt(promotion?.peel_max_waiting_time ?? 0);
	const ripped_edge = promotion?.peel_ripped_edge ?? false;
	const custom_ripped_edge = promotion?.custom_ripped_edge ?? false;
	const cover_image_fullscreen = promotion?.peel_cover_image_fullscreen ?? false;

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:20, drawBorder:false };

	const checkIfLoaded = () => {
		if(refs.current['doomy'] == undefined || refs.current['back'] == undefined) return false;
		return true;
	};

	const stage_w = canvas_size.current.w;
	const stage_h = canvas_size.current.h;

	const largest_size = (stage_w > stage_h) ? stage_w : stage_h;
	const doomy_w = Math.max(100, (largest_size / 10));
	const doomy_h = Math.max(100, (largest_size / 10));
	//const doomy_w = stage_w / 10;
	//const doomy_h = stage_h / 10;

	const drag_path_ini = { x:stage_w - doomy_w / 2, y:doomy_h / 2 };
	const drag_path_end = { x:doomy_w / 2, y:stage_h - doomy_h / 2 };

	const total_path_w = drag_path_ini.x - drag_path_end.x;
	const total_path_h = drag_path_end.y - drag_path_ini.y;

	const indexed_images = useMemo(() => {
		const adapted = {};
		for(let img in props.indexed_images) {
			const image = props.indexed_images[img];
			adapted[img] = adaptImageSizes(image, image.image_w, stage_w, stage_h);
		}
		return adapted;
	}, [ props.indexed_images ]);

	const cover_color = promotion?.peel_cover_color ?? 'rgba(180, 0, 0, 1)';
	const cover_image = indexed_images?.peel_cover_image ?? false;
	const back_color = promotion?.peel_back_color ?? 'rgba(180, 100, 0, 1)';
	const back_image = indexed_images?.peel_back_image ?? false;

	const edge_delta = 23.5;
	const edge_add = (ripped_edge) ? 5 : 0;
	const final_back_scale = (back_image !== false) ? back_image_size / Math.min(back_image.image_w, back_image.image_h) : 1;

	const calcFoldAngle = (x, y) => {
		if(checkIfLoaded()) {
			if(x == undefined) x = refs.current['doomy'].x();
			if(y == undefined) y = refs.current['doomy'].y();
		} else {
			if(x == undefined) x = drag_path_ini.x + doomy_w / 2;
			if(y == undefined) y = drag_path_ini.y - doomy_h / 2;
		}

		if(x < 0) {
			// going out
			if(total_path_w > total_path_h) return Math.abs(radToDeg(Math.atan(total_path_w / total_path_h)));
			return Math.abs(radToDeg(Math.atan(total_path_h / total_path_w)));
		}
		
		// still on canvas
		const fold_w = stage_w - (x - doomy_w / 2);
		const fold_h = y + doomy_h / 2;
		return radToDeg(Math.atan(fold_h / fold_w));
	};

	const calcFoldMidPoint = (x, y) => {
		if(checkIfLoaded()) {
			if(x == undefined) x = refs.current['doomy'].x();
			if(y == undefined) y = refs.current['doomy'].y();
		} else {
			if(x == undefined) x = drag_path_ini.x;
			if(y == undefined) y = drag_path_ini.y;
		}

		return {
			x:(stage_w - x) / 2 + doomy_w / 2,
			y:-y / 2 - doomy_h / 2,
		};
	};

	const startTutorial = () => {
		if(is_builder) return;

		clearInterval(tutorial_timeout.current);

		tutorial_timeout.current = setInterval(() => {
			tutorialAnimation();
		}, 6000);

		if(max_waiting_time > 0) {
			clearInterval(tutorial_timeout.current);

			setTimeout(() => {
				if(start_dragging.current === true) return;

				finishDoomy(() => {
					reset();
				});
			}, (max_waiting_time * 1000));
		}
	};

	const reset = (ini_position) => {
		clearInterval(tutorial_timeout.current);
		start_dragging.current = false;

		if(is_builder) resetDoomy(ini_position);
	};

	const handleDragStart = (e) => {
		if(start_dragging.current === false) {
			reset(0);
		} else {
			start_dragging.current = true;
		}
	};

	const calcPercentageX = () => {
		if(!checkIfLoaded()) return 0;

		const actual_x = refs.current['doomy'].x();
		if(parseInt(actual_x) > parseInt(drag_path_ini.x)) return 1;
		if(parseInt(actual_x) < parseInt(drag_path_end.x)) return 0;
		return 1 - Math.max(0, Math.min(1, actual_x / total_path_w));
	};

	const calcPercentageY = () => {
		if(!checkIfLoaded()) return 0;

		const actual_y = refs.current['doomy'].y();
		if(parseInt(actual_y) < parseInt(drag_path_ini.y)) return 0;
		if(parseInt(actual_y) > parseInt(drag_path_end.y)) return 1;
		return Math.max(0, Math.min(1, actual_y / total_path_h));
	};

	const calcPercentage = () => {
		const percent_x = calcPercentageX();
		const percent_y = calcPercentageY();

		if(percent_x > 0.5 && percent_y > 0.5) return Math.max(percent_x, percent_y);
		return Math.min(percent_x, percent_y);
	};

	const handleDragEnd = (e) => {
		(calcPercentage() < 0.5) ? resetDoomy() : finishDoomy();
	};

	const emulateItemsMovement = () => {
		if(!checkIfLoaded()) return;

		refs.current['back'].x(refs.current['doomy'].x());
		refs.current['back'].y(refs.current['doomy'].y() - stage_h);

		const fold_angle = calcFoldAngle();
		const fold_mid_point = calcFoldMidPoint();
		const percent = calcPercentage();

		refs.current['ripped_edge'].rotation(fold_angle);
		refs.current['ripped_edge'].x(fold_mid_point.x - edge_delta + edge_add);
		refs.current['ripped_edge'].y(stage_h + fold_mid_point.y + edge_delta - edge_add);

		refs.current['ripped_shadow'].rotation(fold_angle);
		refs.current['ripped_shadow'].x(fold_mid_point.x - edge_delta + edge_add);
		refs.current['ripped_shadow'].y(stage_h + fold_mid_point.y + edge_delta - edge_add);
		refs.current['ripped_shadow'].shadowBlur(40 + 20 * percent);

		refs.current['back_img'].shadowOpacity(0.4 + 0.2 * percent);
		refs.current['back_img'].shadowBlur(7 + 40 * percent);
	};

	const handleDragMove = (e) => {
		if(!checkIfLoaded()) return;

		const actual_x = refs.current['doomy'].x();
		const actual_y = refs.current['doomy'].y();

		const min_x = (doomy_w / 2);
		const max_x = (stage_w - doomy_w / 2);

		const min_y = (doomy_h / 2);
		const max_y = (stage_h - doomy_h / 2);

		const final_x = Math.max(Math.min(actual_x, max_x), min_x);
		const final_y = Math.max(Math.min(actual_y, max_y), min_y);

		refs.current['doomy'].x(final_x);
		refs.current['doomy'].y(final_y);

		emulateItemsMovement();
	};

	const finishMe = () => {
		if(is_builder) {
			reset();
			return;
		}

		refs.current['layer'].clear();
		refs.current['main_stage'].clear();
		if(props.onFinish != undefined) props.onFinish();
	};

	const animateTo = (x, y, duration, cb, easing = 'StrongEaseOut') => {
		refs.current['doomy'].to({
			x,
			y,
			duration,
			easing:Konva.Easings[easing],
		});

		const fold_angle = calcFoldAngle(x, y);
		const fold_mid_point = calcFoldMidPoint(x, y);
		refs.current['ripped_edge'].to({
			x:fold_mid_point.x - edge_delta + edge_add,
			y:stage_h + fold_mid_point.y + edge_delta - edge_add,
			rotation:fold_angle,
			duration,
			easing:Konva.Easings[easing],
		});
		refs.current['ripped_shadow'].to({
			x:fold_mid_point.x - edge_delta + edge_add,
			y:stage_h + fold_mid_point.y + edge_delta - edge_add,
			rotation:fold_angle,
			duration,
			easing:Konva.Easings[easing],
		});

		refs.current['back'].to({
			x,
			y:y - stage_h,
			duration,
			easing:Konva.Easings[easing],
			onFinish:() => {
				if(cb != undefined) cb();
			},
		});
	};

	const resetDoomy = (duration = 1) => {
		if(!checkIfLoaded()) return;

		animateTo(drag_path_ini.x, drag_path_ini.y, duration);
	};

	const finishDoomy = (cb) => {
		if(!checkIfLoaded()) return;

		animateTo(drag_path_end.x, drag_path_end.y, 0.3, () => {
			refs.current['doomy'].to({
				x:-stage_w,
				y:stage_h,
				duration:0.7,
			});

			refs.current['back'].to({
				x:-stage_w,
				y:stage_h,
				duration:0.7,
				onFinish:() => {
					finishMe();

					if(cb != undefined) cb();
				},
			});
		}, 'StrongEaseIn');
	};

	const tutorialAnimation = () => {
		if(start_dragging.current) return;
		if(!checkIfLoaded()) return;

		animateTo((drag_path_ini.x - 30), (drag_path_ini.y + 30), 0.5, () => {
			animateTo((drag_path_ini.x - 15), (drag_path_ini.y + 15), 0.25, () => {
				animateTo((drag_path_ini.x - 30), (drag_path_ini.y + 30), 0.25, () => {
					resetDoomy(0.5);
				});
			});
		});
	};

	useEffect(() => {
		canvas_size.current = props.canvas_size;

		emulateItemsMovement();

		if(refs.current['cover_img'] != undefined) refs.current['cover_img'].cache(default_cache_props);
		refs.current['back_img'].cache(default_cache_props);

		refs.current['layer'].draw();

		startTutorial();
	}, [ props.canvas_size ]);

	useEffect(() => {
		if(refs.current['cover_img'] != undefined) refs.current['cover_img'].cache(default_cache_props);
		if(refs.current['back_img'] != undefined) refs.current['back_img'].cache(default_cache_props);
	}, [
		ripped_edge,
		back_color,
		cover_image_fullscreen,
		props.promotion?.full_peel_cover_image,
		props.promotion?.full_peel_back_image,
	]);

	if(promotion === false) return null;

	const getEdgeData = (is_shadow = false) => {
		if(!ripped_edge && is_shadow) return "M0,10l-1000,2075l2000,500l2000-500L2000,10H0z";
		if(!ripped_edge && !is_shadow) return "M-2500,0v-3400h7340V0H-2500z";

		if(is_shadow) return "M0,10l-1000,2075l2000,500l2000-500L2000,10l-45.4-0.1c-11.4,0-11.4,0.8-22.7,0.8c-11.4,0-11.4-1.9-22.7-1.9c-11.4,0-11.4,2.8-22.7,2.8c-11.4,0-11.4-2.8-22.7-2.8c-11.4,0-11.4,0.2-22.7,0.2s-11.4,3.5-22.7,3.5s-11.4-2.3-22.7-2.3c-11.4,0-11.4-4-22.7-4S1761.4,6,1750,6s-11.4,5-22.7,5c-11.4,0-11.4-2.9-22.7-2.9c-11.4,0-11.4-3.1-22.7-3.1s-11.4,4.3-22.7,4.3c-11.4,0-11.4-1.8-22.7-1.8c-11.4,0-11.4-0.2-22.7-0.2c-11.4,0-11.4,2.1-22.7,2.1c-11.4,0-11.4,0-22.7,0c-11.4,0-11.4,2.8-22.7,2.8c-11.4,0-11.4-4.5-22.7-4.5c-11.4,0-11.4,2.2-22.7,2.2s-11.4-2.2-22.7-2.2s-11.9,2.1-22.7,3.7c-6.9,1-11.4,1.7-22.7,1.7c-11.4,0-11.4-3-22.7-3c-11.4,0-11.4-0.3-22.7-0.3s-11.4,1.9-22.7,1.9s-11.4-0.7-22.7-0.7c-11.4,0-11.4,3.1-22.7,3.1s-11.4-1.6-22.7-1.6c-11.4,0-11.4-5.1-22.7-5.1s-11.4,3.4-22.7,3.4s-11.4,4.2-22.7,4.2c-11.4,0-11.4-4.3-22.7-4.3c-11.4,0-11.4,2.2-22.7,2.2s-11.4-3.8-22.7-3.8s-11.4-0.8-22.7-0.8c-11.4,0-11.4,4.1-22.7,4.1c-11.4,0-11.4,1.9-22.7,1.9s-11.4-6-22.7-6s-11.4,1.1-22.7,1.1c-11.4,0-11.4,2.4-22.7,2.4c-11.4,0-11.4-1.8-22.7-1.8s-11.4-1.8-22.7-1.8c-11.4,0-11.4,2.4-22.7,2.4c-11.4,0-11.4,1.1-22.7,1.1s-11.4-6-22.7-6s-11.4,1.9-22.7,1.9c-11.4,0-11.4,4.1-22.7,4.1c-11.4,0-11.4-0.8-22.7-0.8s-11.4-3.8-22.7-3.8s-11.4,2.2-22.7,2.2c-11.3,0-11.3-4.3-22.6-4.3S761.4,9.2,750,9.2s-11.4,3.4-22.7,3.4s-11.4-5.1-22.7-5.1c-11.4,0-11.4-1.6-22.7-1.6S670.4,9,659.1,9c-11.4,0-11.4-0.7-22.7-0.7s-11.4,1.9-22.7,1.9s-11.4-0.3-22.7-0.3c-11.4,0-11.4-3-22.7-3c-11.4,0-15.8,0.7-22.7,1.7c-10.8,1.5-11.4,3.7-22.7,3.7s-11.4-2.2-22.7-2.2c-11.4,0-11.4,2.2-22.7,2.2c-11.4,0-11.4-4.5-22.7-4.5c-11.4,0-11.4,2.8-22.7,2.8c-11.4,0-11.4,0-22.7,0c-11.4,0-11.4,2.1-22.7,2.1c-11.4,0-11.4-0.2-22.7-0.2c-11.4,0-11.4-1.8-22.7-1.8c-11.4,0-11.4,4.3-22.7,4.3c-11.4,0-11.4-3.1-22.7-3.1c-11.4,0-11.4-2.9-22.7-2.9c-11.4,0-11.4,5-22.7,5s-11.4-0.2-22.7-0.2s-11.4-4-22.7-4c-11.4,0-11.4-2.3-22.7-2.3c-11.4,0-11.4,3.5-22.7,3.5s-11.4,0.2-22.7,0.2S125,8.4,113.6,8.4c-11.4,0-11.4,2.8-22.7,2.8c-11.4,0-11.4-1.9-22.7-1.9c-11.4,0-11.4,0.8-22.7,0.8L0,10z";

		return "M0,10L-2500,0v-3400h7340V0L2000,10l-45.4-0.1c-11.4,0-11.4,0.8-22.7,0.8c-11.4,0-11.4-1.9-22.7-1.9c-11.4,0-11.4,2.8-22.7,2.8c-11.4,0-11.4-2.8-22.7-2.8c-11.4,0-11.4,0.2-22.7,0.2s-11.4,3.5-22.7,3.5s-11.4-2.3-22.7-2.3c-11.4,0-11.4-4-22.7-4S1761.4,6,1750,6s-11.4,5-22.7,5c-11.4,0-11.4-2.9-22.7-2.9c-11.4,0-11.4-3.1-22.7-3.1s-11.4,4.3-22.7,4.3c-11.4,0-11.4-1.8-22.7-1.8c-11.4,0-11.4-0.2-22.7-0.2c-11.4,0-11.4,2.1-22.7,2.1c-11.4,0-11.4,0-22.7,0c-11.4,0-11.4,2.8-22.7,2.8c-11.4,0-11.4-4.5-22.7-4.5c-11.4,0-11.4,2.2-22.7,2.2s-11.4-2.2-22.7-2.2s-11.9,2.1-22.7,3.7c-6.9,1-11.4,1.7-22.7,1.7c-11.4,0-11.4-3-22.7-3c-11.4,0-11.4-0.3-22.7-0.3s-11.4,1.9-22.7,1.9s-11.4-0.7-22.7-0.7c-11.4,0-11.4,3.1-22.7,3.1s-11.4-1.6-22.7-1.6c-11.4,0-11.4-5.1-22.7-5.1s-11.4,3.4-22.7,3.4s-11.4,4.2-22.7,4.2c-11.4,0-11.4-4.3-22.7-4.3c-11.4,0-11.4,2.2-22.7,2.2s-11.4-3.8-22.7-3.8s-11.4-0.8-22.7-0.8c-11.4,0-11.4,4.1-22.7,4.1c-11.4,0-11.4,1.9-22.7,1.9s-11.4-6-22.7-6s-11.4,1.1-22.7,1.1c-11.4,0-11.4,2.4-22.7,2.4c-11.4,0-11.4-1.8-22.7-1.8s-11.4-1.8-22.7-1.8c-11.4,0-11.4,2.4-22.7,2.4c-11.4,0-11.4,1.1-22.7,1.1s-11.4-6-22.7-6s-11.4,1.9-22.7,1.9c-11.4,0-11.4,4.1-22.7,4.1c-11.4,0-11.4-0.8-22.7-0.8s-11.4-3.8-22.7-3.8s-11.4,2.2-22.7,2.2S785.3,5,774,5s-12.6,4.2-24,4.2s-11.4,3.4-22.7,3.4s-11.4-5.1-22.7-5.1c-11.4,0-11.4-1.6-22.7-1.6S670.4,9,659.1,9c-11.4,0-11.4-0.7-22.7-0.7s-11.4,1.9-22.7,1.9S602.3,9.9,591,9.9c-11.4,0-11.4-3-22.7-3c-11.4,0-15.8,0.7-22.7,1.7c-10.8,1.5-11.4,3.7-22.7,3.7s-11.4-2.2-22.7-2.2c-11.4,0-11.4,2.2-22.7,2.2c-11.4,0-11.4-4.5-22.7-4.5c-11.4,0-11.4,2.8-22.7,2.8c-11.4,0-11.4,0-22.7,0c-11.4,0-11.4,2.1-22.7,2.1c-11.4,0-11.4-0.2-22.7-0.2c-11.4,0-11.4-1.8-22.7-1.8c-11.4,0-11.4,4.3-22.7,4.3c-11.4,0-11.4-3.1-22.7-3.1c-11.4,0-11.4-2.9-22.7-2.9c-11.4,0-11.4,5-22.7,5s-11.4-0.2-22.7-0.2s-11.4-4-22.7-4c-11.4,0-11.4-2.3-22.7-2.3C171,7.5,171,11,159.7,11s-11.4,0.2-22.7,0.2s-12-2.8-23.4-2.8s-11.4,2.8-22.7,2.8c-11.4,0-11.4-1.9-22.7-1.9c-11.4,0-11.4,0.8-22.7,0.8L0,10z";
	};

	const getRippedData = () => {
		if(!ripped_edge) return 'M0 0h1000v1000H0z';

		if(custom_ripped_edge !== false) return custom_ripped_edge;

		// STEPS TO CREATE A CUSTOM RIPPED EDGE:
		// 1. create a 1000x1000px Adobe Illustrate file
		// 2. add the back_image to it (it should fill the whole document canvas)
		// 3. draw the edge you want (only will display the left and bottom edges) and close the path in the top-right corner
		// 4. copy the <path /> data field from SVG exporter utility from Illustrator and return it from here
		return 'M1000,1000c0,0-43.4-2.2-59.3-2s-57.5,0.2-73.3-2.9c-15.9-3-61.2,0.3-73-1.7c-11.8-2-83.5,4.4-90.2,3.3c-6.8-1.1-56.1,3.3-81.8,0c-25.7-3.3-110.5,0.1-113.2,0c-2.7-0.1-11.2-1.6-17.9-0.3s-89.2-2.7-113.2-1.7c-24,1-48.7-1-66.9-2.4c-2.4-2-16.6-3.4-21.6-0.3c-4.1,0.7-65.5,6.5-80.8,2.9c-19.7-4.7-50.1,1.5-56.9-0.9c-6.8-2.4-75,1.5-99.6,0.3c-16.3-0.8-27.5,1.2-36.8-9.7c-16.4-19.1-8.9-62.5-9.1-68.5c0.7-4.9-0.8-6.3-1.7-9.6c-1.4-5.4-0.7-46.3,0.4-59.2s-1.4-42.2-1.7-43.9c-0.3-1.7,1-22.6,0-40.9s3.1-128.1-0.1-135.2c1.2-1.4-0.1-2.1-0.1-2.1S1.1,577.9,1.1,575c0-2.9-0.9-62.9-0.3-70.4s0.4-124.1-0.2-131.4c-0.6-7.2,2.8-73.9,2.5-77.2C2.7,292.9,4,244.4,2,233.8C0,223.3,1.6,115.8,2,98.4S0,0,0,0h1000V1000z';
	};

	const stage_classes = ['canvas_cont'];

	let cover_image_props = {};
	if(cover_image !== false) {
		cover_image_props = {
			fillPatternImage:cover_image.fillPatternImage,
			fillPatternRepeat:'no-repeat',
		};

		if(cover_image_fullscreen) {
			const scale = Math.max((stage_w / cover_image.image_w), (stage_h / cover_image.image_h));

			cover_image_props.width = scale * cover_image.image_w;
			cover_image_props.height = scale * cover_image.image_h;
			cover_image_props.fillPatternScaleX = scale;
			cover_image_props.fillPatternScaleY = scale;
			cover_image_props.x = (stage_w - scale * cover_image.image_w) / 2;
			cover_image_props.y = (stage_h - scale * cover_image.image_h) / 2;
		} else {
			cover_image_props.width = Math.min(stage_w, cover_image.image_w);
			cover_image_props.height = Math.min(stage_h, cover_image.image_h);
			cover_image_props.x = (stage_w - cover_image.image_w) / 2;
			cover_image_props.y = (stage_h - cover_image.image_h) / 2;
		}
	}

	const back_image_scale = Math.max(stage_w / back_image_size, stage_h / back_image_size);

	return (
		<Stage
			ref={(node) => { refs.current['main_stage'] = node; }}
			className={stage_classes.join(" ")}
			width={stage_w}
			height={stage_h}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Group>
					<Rect
						width={stage_w}
						height={stage_h}
						fill={cover_color}
					/>

					{(cover_image !== false) && (
						<Rect
							ref={(node) => { refs.current['cover_img'] = node; }}
							{...cover_image_props}
						/>
					)}
				</Group>

				<Group
					ref={(node) => { refs.current['back'] = node; }}
					x={drag_path_ini.x}
					y={drag_path_ini.y - stage_h}
					offsetX={doomy_w / 2}
					offsetY={-doomy_h / 2}
					width={stage_w}
					height={stage_h}
				>
					{(back_image === false) && (
						<Path
							ref={(node) => { refs.current['back_img'] = node; }}
							width={stage_w}
							height={stage_h}
							fill={back_color}
							offsetY={back_image_size}
							y={stage_h}
							shadowBlur={20}
							shadowColor='black'
							shadowOpacity={1}
							data={getRippedData()}
						/>
					)}

					{(back_image !== false) && (
						<Path
							ref={(node) => { refs.current['back_img'] = node; }}
							width={back_image_size}
							height={back_image_size}
							//width={back_image.image_w * final_back_scale}
							//height={back_image.image_h * final_back_scale}
							fillPatternImage={back_image.fillPatternImage}
							fillPatternRepeat='no-repeat'
							fillPatternScaleX={final_back_scale}
							fillPatternScaleY={final_back_scale}
							offsetY={back_image_size}
							scaleX={back_image_scale}
							scaleY={back_image_scale}
							y={stage_h}
							shadowBlur={20}
							shadowColor='black'
							shadowOpacity={1}
							data={getRippedData()}
						/>
					)}

					<Path
						ref={(node) => { refs.current['ripped_edge'] = node; }}
						width={2000}
						height={2000}
						offsetX={1000}
						fill="black"
						data={getEdgeData()}
						globalCompositeOperation='xor'
					/>

					<Path
						ref={(node) => { refs.current['ripped_shadow'] = node; }}
						width={2000}
						height={2000}
						offsetX={1000}
						fill='white'
						data={getEdgeData(true)}
						shadowBlur={50}
						shadowColor='black'
						shadowOpacity={0.7}
						globalCompositeOperation='destination-over'
					/>
				</Group>

				<Group
					ref={(node) => { refs.current['doomy'] = node; }}
					x={drag_path_ini.x}
					y={drag_path_ini.y}
					offsetX={doomy_w / 2}
					offsetY={doomy_h / 2}
					opacity={0}
					draggable={true}
					onDragStart={(e) => handleDragStart(e)}
					onDragEnd={(e) => handleDragEnd(e)}
					onDragMove={(e) => handleDragMove(e)}
				>
					<Rect
						width={0.65 * stage_w}
						height={0.65 * stage_h}
						x={-0.65 * stage_w + doomy_w}
						stroke='red'
						strokeWidth={2}
					/>

					<Rect
						width={doomy_w}
						height={doomy_h}
						stroke='red'
						strokeWidth={2}
					/>
				</Group>
			</Layer>
		</Stage>
	)
};

export default PeelMeCanvas;